import { useId } from "react"

import { ReactComponent as IconChevronDown } from "@common_assets/svg/chevron-down.svg"
import { ReactComponent as IconFilters } from "@common_assets/svg/filters.svg"
import { Box, Button, Chip, Stack, Typography } from "@mui/material"
import IconWrapper from "components/common/icon/iconWrapper"
import { useTranslation } from "react-i18next"
import { DashboardTab } from "ts/types/AdminDashboard"

import CustomRangeDialog from "./CustomRangeDialog"
import { filterChipSx } from "./FilterBar.styles"
import useDashboardPeriod, { DASHBOARD_PRESETS } from "./useDashboardPeriod"

const FilterBar = ({ tab }: { tab: DashboardTab }) => {
  const { t } = useTranslation()
  const { params, setPreset, setCustomRange } = useDashboardPeriod(tab)
  const timeSpanLabelId = useId()

  return (
    // Labels share one column so the chips strip and the scope select line up
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { mobile: "1fr", tablet: "max-content 1fr" },
        alignItems: "center",
        columnGap: 3,
        rowGap: 1.5,
      }}
    >
      <Typography
        variant="body1"
        color="mockup.neutral30"
        fontWeight={700}
        id={timeSpanLabelId}
      >
        {t("adminDashboard.timeSpan")}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
        sx={{ bgcolor: "mockup.primary95", p: 0.5, borderRadius: "20px" }}
      >
        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          role="group"
          aria-labelledby={timeSpanLabelId}
        >
          {DASHBOARD_PRESETS.map((preset) => (
            <Chip
              key={preset}
              label={t(`adminDashboard.preset_${preset}`)}
              color={params.preset === preset ? "primary" : "default"}
              variant={params.preset === preset ? "filled" : "outlined"}
              onClick={() => setPreset(preset)}
              aria-pressed={params.preset === preset}
              sx={filterChipSx(params.preset === preset)}
            />
          ))}
        </Stack>
        <Stack direction="row" alignItems="center" gap={1} ml="auto">
          <Typography variant="body1" color="mockup.neutral10" fontWeight={500}>
            {t("adminDashboard.custom")}
          </Typography>
          <CustomRangeDialog
            key={`${params.date_from}-${params.date_to}`}
            params={params}
            onApply={setCustomRange}
          />
        </Stack>
      </Stack>
      {tab === "overview" && (
        <>
          <Typography variant="body1" color="mockup.neutral30" fontWeight={700}>
            {t("adminDashboard.scope")}
          </Typography>
          {/* BED has no scope param yet, so this is a placeholder only */}
          <Button
            variant="text"
            color="inherit"
            disabled
            startIcon={
              <IconWrapper component="span" display="flex" aria-hidden>
                <IconFilters width="24px" height="24px" />
              </IconWrapper>
            }
            endIcon={
              <IconWrapper
                component="span"
                display="flex"
                aria-hidden
                color="inherit"
              >
                <IconChevronDown />
              </IconWrapper>
            }
            sx={{
              height: "38px",
              px: 2,
              border: "none",
              borderRadius: "16px",
              bgcolor: "mockup.primary95",
              justifyContent: "flex-start",
              "& .MuiButton-endIcon": { ml: "auto" },
              fontSize: "16px",
              textTransform: "none",
              "&.Mui-disabled": {
                bgcolor: "mockup.primary95",
                color: "mockup.neutral10",
              },
            }}
          >
            {t("adminDashboard.allClassesAndGroups")}
          </Button>
        </>
      )}
    </Box>
  )
}

export default FilterBar
